<section class="cost-comparison">
  @if (isLoading()) {
    <app-loading />
  } @else if (providerCostCards().length === 0) {
    <div class="cost-comparison-empty">
      <i class="pi pi-info-circle empty-icon"></i>
      <h3 class="empty-title">
        {{ 'project.steps.overallResults.noData.title' | transloco }}
      </h3>
      <p class="empty-message">
        {{ 'project.steps.overallResults.noData.message' | transloco }}
      </p>
    </div>
  } @else {
    <div class="section-group">
      @for (provider of providerCostCards(); track provider.hrToolDirectusId) {
        <article class="summary-card">
          <header class="summary-card-header">
            <h3 class="provider-name">{{ provider.translatedName }}</h3>
            <div class="provider-total-cost">
              <span class="provider-total-cost-value">
                {{
                  provider.totalCost
                    | appCurrency
                      : {locale: provider.currencyLocale, currencyCode: provider.currencyCode}
                      : 0
                      : 0
                }}
              </span>
              <span class="provider-total-cost-label">{{
                'project.steps.overallResults.card.totalCost' | transloco
              }}</span>
            </div>
          </header>

          <div class="metrics-grid">
            @for (metric of provider.metrics; track metric.labelKey) {
              <div class="metric-box">
                <div class="metric-header">
                  <span
                    class="metric-icon-wrap"
                    [ngClass]="'metric-icon-wrap--' + metric.iconAccent"
                  >
                    <i [class]="metric.icon + ' metric-icon'"></i>
                  </span>
                  <span class="metric-label">{{ metric.labelKey | transloco }}</span>
                  <app-directus-tooltip
                    tooltipStyleClass="tooltip-bg-second-level"
                    [tooltipKey]="metric.tooltipKey"
                  ></app-directus-tooltip>
                </div>
                @if (metric.type === 'currency') {
                  <span class="metric-value">
                    {{
                      metric.value
                        | appCurrency
                          : {locale: provider.currencyLocale, currencyCode: provider.currencyCode}
                          : 0
                          : 0
                    }}
                  </span>
                } @else if (metric.type === 'percentage') {
                  <span class="metric-value metric-value--percentage">
                    {{ metric.value | appNumber: '1.1-1' }}%
                  </span>
                } @else {
                  <span class="metric-value">{{ metric.value | appNumber: '1.1-1' }}</span>
                }
              </div>
            }
          </div>
        </article>
      }
    </div>

    <div class="section-header">
      <h3>{{ 'project.steps.overallResults.costComparison.costDevelopment.title' | transloco }}</h3>
    </div>

    <article class="chart-card">
      <header class="chart-card-header">
        <div class="chart-filters">
          <div class="chart-filter-field">
            <span class="chart-filter-label">{{ 'project.steps.rfq.costType' | transloco }}</span>
            <p-multiSelect
              [ngModel]="selectedCostTypes()"
              (ngModelChange)="selectedCostTypes.set($event ?? [])"
              [options]="costTypeFilterOptions"
              optionValue="value"
              [showToggleAll]="false"
              [filter]="false"
              [showHeader]="false"
              [maxSelectedLabels]="0"
              [selectedItemsLabel]="'{0} selected'"
              appendTo="body"
              class="chart-filter-multiselect"
            >
              <ng-template #item let-option>
                {{ option.labelKey | transloco }}
              </ng-template>
            </p-multiSelect>
          </div>

          <div class="chart-filter-field">
            <span class="chart-filter-label">HR-tool</span>
            <p-multiSelect
              [ngModel]="selectedHrToolIds()"
              (ngModelChange)="selectedHrToolIds.set($event ?? [])"
              [options]="hrToolFilterOptions()"
              optionLabel="label"
              optionValue="value"
              [showToggleAll]="false"
              [filter]="false"
              [showHeader]="false"
              [maxSelectedLabels]="1"
              [selectedItemsLabel]="'{0} selected'"
              appendTo="body"
              class="chart-filter-multiselect"
            />
          </div>
        </div>
        <div class="chart-view-field">
          <span class="chart-filter-label">{{ 'common.view' | transloco }}</span>
          <div class="chart-view-toggle" role="group" aria-label="Cost development chart view">
            <button
              type="button"
              class="chart-view-button"
              [class.chart-view-button--active]="costDevelopmentChartView() === 'years'"
              (click)="costDevelopmentChartView.set('years')"
            >
              {{ 'common.years' | transloco }}
            </button>
            <button
              type="button"
              class="chart-view-button"
              [class.chart-view-button--active]="costDevelopmentChartView() === 'total'"
              (click)="costDevelopmentChartView.set('total')"
            >
              {{ 'common.total' | transloco }}
            </button>
          </div>
        </div>
      </header>
      <div class="chart-wrapper">
        @if (costDevelopmentChartView() === 'years') {
          <p-chart type="bar" [data]="groupedStackedChartData()" [options]="chartOptions" />
        } @else {
          <p-chart type="bar" [data]="totalStackedChartData()" [options]="totalChartOptions" />
        }
      </div>
      <div class="chart-custom-legend chart-custom-legend--bottom">
        @for (legendItem of chartLegendItems(); track legendItem.providerName) {
          <span class="chart-custom-legend-item">
            <span
              class="chart-custom-legend-swatch"
              [style.backgroundColor]="legendItem.color"
            ></span>
            <span class="chart-custom-legend-label">{{ legendItem.providerName }}</span>
          </span>
        }
      </div>
    </article>

    <div class="section-header">
      <h3>
        {{ 'project.steps.overallResults.costComparison.detailedBreakdown.title' | transloco }}
      </h3>
    </div>

    <article class="table-card">
      <div class="table-wrapper">
        <table class="breakdown-table">
          <thead>
            <tr>
              <th class="category-column">{{ 'common.category' | transloco }}</th>
              @for (year of detailedBreakdownYears(); track year) {
                <th>{{ 'common.year' | transloco }} {{ year }}</th>
              }
              <th>{{ 'common.total' | transloco }}</th>
            </tr>
          </thead>
          <tbody>
            @for (row of combinedDetailedBreakdownRows(); track row.id) {
              @if (row.isFirstProviderRow) {
                <tr class="provider-group-row">
                  <td
                    [attr.colspan]="detailedBreakdownYears().length + 2"
                    [style.color]="row.providerColor"
                  >
                    {{ row.providerName }}
                  </td>
                </tr>
              }
              <tr [class.total-row]="row.isTotalRow">
                <td class="category-cell">{{ row.labelKey | transloco }}</td>
                @for (value of row.values; track $index) {
                  <td>
                    {{
                      value
                        | appCurrency
                          : {locale: row.currencyLocale, currencyCode: row.currencyCode}
                          : 0
                          : 0
                    }}
                  </td>
                }
                <td>
                  {{
                    row.total
                      | appCurrency
                        : {locale: row.currencyLocale, currencyCode: row.currencyCode}
                        : 0
                        : 0
                  }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </article>
  }
</section>
